import {apiClient} from './axios.ts';
import {PaginationData} from '../models/paginationData.ts';
import {
    AdminBatchJob,
    AdminBatchJobDetail,
    AdminBatchJobStatus,
    AdminBatchJobType,
    AdminBatchScopeType,
    AdminBatchSubmission,
    ExecutionMode,
} from '../types/adminBatch.ts';

export interface AdminBatchJobFilterParams {
    page: number;
    size: number;
    jobType?: AdminBatchJobType;
    scopeType?: AdminBatchScopeType;
    scopeId?: number;
    status?: AdminBatchJobStatus;
}

export interface AdminBatchJobDetailParams {
    submissionPage?: number;
    submissionSize?: number;
    generatedWordPage?: number;
    generatedWordSize?: number;
}

export const adminBatchApi = {
    getJobs: (params: AdminBatchJobFilterParams) => {
        const {page, size, jobType, scopeType, scopeId, status} = params;
        return apiClient.get<PaginationData<AdminBatchJob>>('/admin/batch/jobs', {
            page: page - 1,
            size,
            ...(jobType ? {jobType} : {}),
            ...(scopeType ? {scopeType} : {}),
            ...(scopeId !== undefined ? {scopeId} : {}),
            ...(status ? {status} : {}),
        });
    },
    getJobDetail: (jobId: number, params?: AdminBatchJobDetailParams) => {
        const {
            submissionPage = 0,
            submissionSize = 50,
            generatedWordPage = 0,
            generatedWordSize = 100,
        } = params ?? {};
        return apiClient.get<AdminBatchJobDetail>(`/admin/batch/jobs/${jobId}`, {
            submissionPage,
            submissionSize,
            generatedWordPage,
            generatedWordSize,
        });
    },
    cancelJob: (jobId: number) => {
        return apiClient.post<AdminBatchJob>(`/admin/batch/jobs/${jobId}/cancel`);
    },
    cancelSubmission: (jobId: number, submissionId: number) => {
        return apiClient.post<AdminBatchSubmission>(`/admin/batch/jobs/${jobId}/submissions/${submissionId}/cancel`);
    },
    retrySubmissionNextProvider: (jobId: number, submissionId: number) => {
        return apiClient.post<AdminBatchSubmission>(`/admin/batch/jobs/${jobId}/submissions/${submissionId}/retry-next-provider`);
    },
    recheckSubmissionNow: (jobId: number, submissionId: number) => {
        return apiClient.post<{
            jobId: number;
            submissionId: number;
            previousStatus: 'SUBMITTED' | 'COMPLETED' | 'FAILED';
            currentStatus: 'SUBMITTED' | 'COMPLETED' | 'FAILED';
            action: string;
            externalStatus?: 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
        }>(`/admin/batch/jobs/${jobId}/submissions/${submissionId}/recheck-now`);
    },
    reprocessAllCached: (jobId: number) => {
        return apiClient.post<{
            jobId: number;
            totalSubmissions: number;
            processed: number;
            newGenerated: number;
            skipped: number;
            failed: number;
            noCache: number;
        }>(`/admin/batch/jobs/${jobId}/reprocess-all-cached`);
    },
    reprocessCachedSubmission: (jobId: number, submissionId: number) => {
        return apiClient.post<{
            submissionId: number;
            previousStatus: string;
            currentStatus: string;
            generated: number;
            skipped: number;
            failed: number;
            errorMessage?: string;
        }>(`/admin/batch/jobs/${jobId}/submissions/${submissionId}/reprocess-cached`);
    },
    resolveStaleSubmissions: (
        jobId: number,
        staleMinutes: number,
        action: 'CANCEL' | 'RETRY_NEXT_PROVIDER',
    ) => {
        return apiClient.post<{
            jobId: number;
            action: 'CANCEL' | 'RETRY_NEXT_PROVIDER';
            staleMinutes: number;
            scanned: number;
            matched: number;
            canceled: number;
            retried: number;
            errors: number;
            pendingRemaining: number;
        }>(
            `/admin/batch/jobs/${jobId}/resolve-stale-submissions?staleMinutes=${staleMinutes}&action=${action}`,
        );
    },
    retryFailedSubmissions: (jobId: number, executionMode: ExecutionMode) => {
        return apiClient.post<{ jobId: number; executionMode: ExecutionMode; created: number }>(
            `/admin/batch/jobs/${jobId}/retry-failed?executionMode=${executionMode}`,
        );
    },
    retrySubmission: (
        jobId: number,
        submissionId: number,
        executionMode: ExecutionMode,
        provider?: string,
    ) => {
        const params = new URLSearchParams({executionMode});
        if (provider) params.set('provider', provider);
        return apiClient.post<AdminBatchSubmission>(
            `/admin/batch/jobs/${jobId}/submissions/${submissionId}/retry?${params.toString()}`,
        );
    },
};
